import React, { useCallback, useMemo, useState } from 'react'
import Modal from 'core/elements/modal'
import Button from 'core/elements/button'
import ShowActionState, { ActionStateResult } from 'core/components/ShowActionState'
import useDeleteAction from 'core/hooks/useDeleteAction'
import DeleteAction from 'core/actions/DeleteAction'
import { IDataKeys } from 'k8s/datakeys.model'
import Info from 'core/components/validatedForm/Info'

interface CustomDeleteDialogProps {
  rows: any[]
  onClose: (value: boolean) => void
  title?: string
  maxWidth?: number
  subTitle?: string
  deleteAction: DeleteAction<keyof IDataKeys>
  resourceLabel: string
  showInfo?: boolean
  infoMessage?: string | React.ReactNode
  actionNames?: {
    inProgress?: string
    success?: string
    failed?: string
  }
  buildBodyFn?: (row: any) => Record<string, any>
  parentId?: string //if the id is same for all rows like we have in router interfaces page
  getRowId?: (row: any) => string // optional override for id
  getRowName?: (row: any) => string // optional override for name
  onSuccess?: () => void // optional callback on successful delete
  disableSubmit?: boolean
}

/**
 * CustomDeleteDialog Component
 *
 * Objective:
 * This component is an extended, more flexible version of `DeleteDialog` that handles
 * advanced deletion scenarios in bulk.
 *
 * It:
 * - Displays a modal dialog asking for confirmation before deleting multiple resources.
 * - Uses `useDeleteAction` to perform the delete API calls and show per-item progress/results.
 * - Supports optional `buildBodyFn` to customize the request body for each delete call.
 * - Allows passing a `parentId` (useful when all rows share a common parent, e.g. router interfaces).
 * - Supports custom ID and name resolution through `getRowId` and `getRowName`.
 * - Optionally displays an `Info` message at the top of the dialog for additional warnings/notes.
 * - Handles bulk deletion status (In Progress, Success, Failed) and displays results using `ShowActionState`.
 *
 * Typical usage:
 * - Use this when a simple `DeleteDialog` is not enough (e.g. when you need parent IDs,
 *   custom body payloads, or additional warning messages).
 *
 * Example:
 * <CustomDeleteDialog
 *   rows={selectedInterfaces}
 *   deleteAction={deleteRouterInterfaceAction}
 *   onClose={() => setShowDelete(false)}
 *   resourceLabel="interfaces"
 *   showInfo
 *   infoMessage="Deleting an interface will detach it from the router."
 *   parentId={routerId}
 *   getRowId={(row) => row.portId}
 *   getRowName={(row) => row.portIdName}
 * />
 */
export default function CustomDeleteDialog({
  rows,
  onClose,
  title,
  maxWidth = 600,
  subTitle,
  deleteAction,
  resourceLabel,
  showInfo = false,
  infoMessage,
  actionNames = {
    inProgress: 'Deleting',
    success: 'Deleted',
    failed: 'Delete Failed',
  },
  buildBodyFn: buildBodyFn,
  parentId,
  getRowId,
  getRowName,
  onSuccess,
  disableSubmit = false,
}: CustomDeleteDialogProps) {
  const { bulkRemove } = useDeleteAction(deleteAction)

  const idToNameMap = useMemo(() => {
    return rows?.reduce((acc, row) => {
      const uiId = getRowId ? getRowId(row) : row?.id
      const uiName = getRowName ? getRowName(row) : row?.name || uiId
      acc[uiId] = uiName
      return acc
    }, {} as Record<string, string>)
  }, [rows, getRowId, getRowName])

  const [bulkResults, setBulkResults] = useState<Array<ActionStateResult>>(
    rows?.map((row) => {
      const uiId = getRowId ? getRowId(row) : row?.id
      const uiName = getRowName ? getRowName(row) : row?.name || uiId
      return {
        name: uiName,
        status: '' as const,
        actionName: '',
      }
    }),
  )

  const handleDelete = useCallback(async () => {
    // 🔹 initial results
    const initialResults: ActionStateResult[] = rows?.map((row) => {
      const uiId = getRowId ? getRowId(row) : row?.id
      const uiName = getRowName ? getRowName(row) : row?.name || uiId
      return {
        name: uiName,
        status: 'In Progress',
        actionName: actionNames.inProgress ?? 'Deleting',
      }
    })
    setBulkResults(initialResults)

    // 🔹 apply buildBodyFn if provided
    const rowsWithBody = rows.map((item) => {
      const base = buildBodyFn ? buildBodyFn(item) : {}
      return {
        ...base, // flatten into top-level
        id: getRowId ? getRowId(item) : item?.id,
        parentId,
        body: base,
      }
    })
    const results = await bulkRemove(rowsWithBody)

    const toActionStateResult = (res: any): ActionStateResult => {
      const resId = res?.params?.id
      const name = idToNameMap[resId] ?? resId ?? 'Unknown'
      return {
        name,
        status: res?.success ? 'Success' : 'Failed',
        actionName: res?.success
          ? actionNames.success ?? 'Deleted'
          : actionNames.failed ?? 'Delete Failed',
        error: res?.success ? null : res?.error?.message,
      }
    }
    setBulkResults(results?.map(toActionStateResult))
    if (results?.every((res) => res?.success) && onSuccess) {
      onSuccess()
    }
  }, [rows, bulkRemove, idToNameMap, actionNames, buildBodyFn, parentId, onSuccess])

  const handleClose = useCallback(
    (success?: boolean) => {
      setBulkResults([])
      onClose(success)
    },
    [onClose],
  )

  const showFinishButton = useMemo(() => bulkResults?.some((r) => !!r?.status), [bulkResults])

  return (
    <Modal
      panel="dialog"
      open
      onClose={() => handleClose()}
      title={title}
      maxWidth={maxWidth}
      footer={
        showFinishButton ? (
          <Button variant="secondary" onClick={() => handleClose()}>
            Finish
          </Button>
        ) : (
          <>
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button disabled={disableSubmit} onClick={handleDelete}>
              Confirm
            </Button>
          </>
        )
      }
    >
      <>
        {showInfo && <Info error>{infoMessage}</Info>}
        <ShowActionState
          title={subTitle ?? `You are about to delete the following ${resourceLabel}:`}
          results={bulkResults}
        />
      </>
    </Modal>
  )
}
